import { SERVICES, DAYS, TIMES, STATUSES, initialBookings, isAvailable, createBooking, advanceBooking, bookingCounts, filteredBookings, loadSampleBookings, visibleBookingSelection } from './demo-booking-model.mjs';

/** Isolated, browser-memory-only workspace. No network or persistent storage. */
export function initAppWorkspace(root, onLayout = () => {}) {
  if (!root || root.dataset.ready) return;
  root.dataset.ready = 'true';
  const one = selector => root.querySelector(selector);
  const all = selector => [...root.querySelectorAll(selector)];
  const el = (tag, text, className) => {
    const node = document.createElement(tag);
    if (text !== undefined) node.textContent = text;
    if (className) node.className = className;
    return node;
  };
  let state = initialBookings(), filter = 'All', selectedId = null, latestId = null;
  let choice = { service: SERVICES[0], day: DAYS[0], time: '' };
  const announce = message => { one('[data-app-announcement]').textContent = message; };
  const chip = status => el('span', status, `app-status app-status--${status.toLowerCase()}`);
  const focusInspector = () => {
    const heading = one('#app-inspector-title');
    heading.focus({ preventScroll: true });
    if (window.matchMedia('(max-width: 1099px)').matches) heading.scrollIntoView({ block: 'start', behavior: 'auto' });
  };
  const setView = (view, focus = false) => {
    all('[data-app-panel]').forEach(panel => { panel.hidden = panel.dataset.appPanel !== view; });
    all('[data-app-view][aria-pressed]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.appView === view)));
    one('[data-app-heading]').textContent = view === 'management' ? 'Booking management' : 'Create a sample booking';
    one('[data-app-subheading]').textContent = view === 'management' ? 'View and manage your sample bookings.' : 'Choose a service and an available time.';
    if (focus) { const heading = one('[data-app-heading]'); heading.tabIndex = -1; heading.focus(); }
    onLayout();
  };

  [['service', SERVICES, '[data-app-services]'], ['day', DAYS, '[data-app-days]'], ['time', TIMES, '[data-app-times]']].forEach(([key, values, selector]) => {
    one(selector).replaceChildren(...values.map(value => {
      const button = el('button', value);
      button.type = 'button'; button.dataset.appChoice = key; button.dataset.value = value;
      button.setAttribute('aria-pressed', 'false'); return button;
    }));
  });
  function renderChoices() {
    all('[data-app-choice]').forEach(button => {
      const { appChoice: key, value } = button.dataset;
      button.setAttribute('aria-pressed', String(choice[key] === value));
      button.disabled = key === 'time' && !isAvailable(state, choice.day, value);
      button.textContent = button.disabled ? `${value} · Booked` : value;
    });
    ['service', 'day', 'time'].forEach(key => { one(`[data-app-summary="${key}"]`).textContent = choice[key] || 'Choose a time'; });
    one('[data-app-create]').disabled = !isAvailable(state, choice.day, choice.time);
  }
  function renderInspector(booking) {
    const details = one('[data-app-booking-details]'); details.replaceChildren();
    if (!booking) {
      details.append(el('p', state.bookings.length ? 'No booking selected. Choose a record from the current list, or try another status filter.' : 'Select a sample booking to see its details and next step.', 'app-inspector-empty'));
      return;
    }
    const info = el('dl', undefined, 'app-detail-fields');
    [['Booking ID', booking.id], ['Service', booking.service], ['Day', booking.day], ['Time', booking.time], ['Guest', booking.guest], ['Status', booking.status]].forEach(([name, value]) => {
      const row = el('div'), dt = el('dt', name), dd = el('dd');
      if (name === 'Status') dd.append(chip(value)); else dd.textContent = value;
      if (name === 'Booking ID') dd.className = 'app-record-id';
      row.append(dt, dd); info.append(row);
    });
    const workflow = el('div', undefined, 'app-detail-workflow');
    workflow.append(el('h5', 'Booking workflow'), el('p', booking.status === 'Completed' ? 'Completed. This sample workflow is finished.' : 'Confirm the booking, then mark it as completed.'));
    const steps = el('ol', undefined, 'app-workflow');
    steps.dataset.status = booking.status.toLowerCase();
    const currentIndex = STATUSES.indexOf(booking.status);
    STATUSES.forEach((status, index) => {
      const step = el('li', status);
      step.dataset.stepState = index === currentIndex ? 'current' : index < currentIndex ? 'done' : 'next';
      if (index === currentIndex) step.setAttribute('aria-current', 'step');
      steps.append(step);
    });
    workflow.append(steps);
    if (booking.status !== 'Completed') {
      const action = booking.status === 'Pending' ? 'Confirm' : 'Complete';
      const button = el('button', `${action} booking`, 'app-primary');
      button.type = 'button'; button.dataset.appAdvance = booking.id;
      button.setAttribute('aria-label', `${action} ${booking.id}`);
      workflow.append(button);
    }
    details.append(info, workflow, el('p', 'This is a sample booking for demonstration only. No real appointment is made.', 'app-detail-note'));
  }
  function renderBookings() {
    const counts = bookingCounts(state);
    one('[data-app-counts]').replaceChildren(...[...STATUSES, 'Total'].map(status => {
      const group = el('div', undefined, `app-count app-count--${status.toLowerCase()}`);
      group.append(el('dt', status), el('dd', String(status === 'Total' ? state.bookings.length : counts[status])));
      return group;
    }));
    const rows = filteredBookings(state, filter);
    const selected = visibleBookingSelection(state, filter, selectedId);
    selectedId = selected?.id || null;
    one('[data-app-visible-count]').textContent = `${rows.length} of ${state.bookings.length} bookings`;
    one('[data-app-booking-list]').replaceChildren(...rows.map(booking => {
      const tr = el('tr'); tr.dataset.appSelect = booking.id;
      tr.dataset.selected = String(booking.id === selectedId);
      const idCell = el('td'), idButton = el('button', booking.id, 'app-record-id');
      idButton.type = 'button'; idButton.dataset.appSelect = booking.id;
      idButton.setAttribute('aria-label', `View ${booking.id}`);
      idButton.setAttribute('aria-controls', 'app-booking-inspector');
      idButton.setAttribute('aria-pressed', String(booking.id === selectedId));
      idCell.append(idButton);
      const statusCell = el('td'); statusCell.append(chip(booking.status));
      const chevron = el('td'), image = el('img');
      image.src = root.dataset.chevronIcon; image.alt = ''; image.width = 18; image.height = 18;
      chevron.append(image); chevron.setAttribute('aria-hidden', 'true');
      tr.append(idCell, el('td', booking.service), el('td', `${booking.day} · ${booking.time}`), statusCell, chevron);
      return tr;
    }));
    one('.app-booking-table').hidden = !rows.length;
    one('[data-app-empty]').hidden = Boolean(rows.length);
    one('[data-app-empty-title]').textContent = state.bookings.length ? 'No bookings with this status.' : 'No sample bookings yet.';
    one('[data-app-empty-body]').textContent = state.bookings.length ? 'Try another status filter to find a sample booking.' : 'Create your first booking, or load three fictional examples to explore the workflow.';
    one('[data-app-load-samples]').hidden = Boolean(state.bookings.length);
    renderInspector(selected);
    onLayout();
  }
  function clearFeedback() {
    latestId = null; one('[data-app-booking-feedback]').textContent = '';
    one('[data-app-manage-created]').hidden = true;
  }
  root.addEventListener('click', event => {
    const target = event.target.closest('button, tr[data-app-select]');
    if (!target || !root.contains(target)) return;
    const data = target.dataset;
    if (data.appView) setView(data.appView, !target.hasAttribute('aria-pressed'));
    if (data.appChoice) {
      choice[data.appChoice] = data.value;
      if (data.appChoice === 'day') choice.time = '';
      clearFeedback(); renderChoices();
    }
    if ('appLoadSamples' in data && !state.bookings.length) {
      state = loadSampleBookings(state); selectedId = state.bookings[0].id;
      filter = 'All'; one('[data-app-filter]').value = 'All';
      renderChoices(); renderBookings();
      announce('Three fictional sample bookings loaded. Nothing was sent or saved.');
      focusInspector();
    }
    if (data.appSelect) {
      selectedId = data.appSelect; renderBookings(); focusInspector();
      announce(`${selectedId} selected.`);
    }
    if ('appCreate' in data) {
      const created = createBooking(state, choice);
      if (!created.booking) return;
      state = created.state; latestId = selectedId = created.booking.id;
      filter = 'All'; one('[data-app-filter]').value = filter;
      one('[data-app-booking-feedback]').textContent = `${latestId} created · Pending. Open Management to confirm it.`;
      one('[data-app-manage-created]').hidden = false;
      renderChoices(); renderBookings();
      one('[data-app-manage-created]').focus({ preventScroll: true });
    }
    if ('appManageCreated' in data) {
      filter = 'All'; one('[data-app-filter]').value = filter; selectedId = latestId;
      renderBookings(); setView('management'); focusInspector();
    }
    if (data.appAdvance) {
      state = advanceBooking(state, data.appAdvance);
      const updated = state.bookings.find(booking => booking.id === data.appAdvance);
      if (!updated) return;
      if (latestId === updated.id) one('[data-app-booking-feedback]').textContent = `${updated.id} · ${updated.status}. ${updated.status === 'Completed' ? 'Sample workflow complete.' : 'Open Management to continue the workflow.'}`;
      renderChoices(); renderBookings(); announce(`${updated.id} is now ${updated.status}.`);
      if (selectedId === updated.id && updated.status !== 'Completed') one('[data-app-advance]')?.focus({ preventScroll: true });
      else if (selectedId === updated.id) focusInspector();
      else one('[data-app-filter]').focus({ preventScroll: true });
    }
    if ('appReset' in data) {
      state = initialBookings(); choice = { service: SERVICES[0], day: DAYS[0], time: '' };
      selectedId = null; filter = 'All'; one('[data-app-filter]').value = filter;
      clearFeedback(); renderChoices(); renderBookings(); setView('booking');
      announce('Sample bookings reset. All demo records have been cleared.');
    }
  });
  one('[data-app-filter]').addEventListener('change', event => {
    filter = event.target.value; renderBookings();
    announce(`${filteredBookings(state, filter).length} sample bookings shown.`);
  });
  renderChoices(); renderBookings(); setView('management');
}
